import React, { useState, useEffect } from "react";
import {
  useNavigate,
  useParams,
  useSearchParams,
  Link,
} from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/ui/Toast";
import { Button, Card } from "../components/ui";

const TIPO_APPARECCHIATURA_OPTIONS = [
  { value: 100000000, label: "Ascensore/Montacarichi", icon: "🛗" },
  { value: 100000001, label: "Caldaia/Centrale termica", icon: "🔥" },
  { value: 100000002, label: "Climatizzazione", icon: "❄️" },
  { value: 100000003, label: "Impianto elettrico", icon: "⚡" },
  { value: 100000004, label: "Impianto antincendio", icon: "🧯" },
  { value: 100000005, label: "Impianto gas medicali", icon: "🏥" },
  { value: 100000006, label: "Gruppo elettrogeno", icon: "🔌" },
  { value: 100000007, label: "Autoclave/Sterilizzatrice", icon: "🧫" },
  { value: 100000008, label: "Apparecchiatura medicale", icon: "💉" },
  { value: 100000009, label: "Impianto idrico/Sanitario", icon: "🚿" },
  { value: 100000011, label: "Cancello/Porta automatica", icon: "🚪" },
  { value: 100000012, label: "Sistema di sicurezza", icon: "🔒" },
  { value: 100000013, label: "Cucina industriale", icon: "🍳" },
  { value: 100000014, label: "Lavanderia industriale", icon: "🧺" },
  { value: 100000099, label: "Altro", icon: "📦" },
];

const STATO_OPTIONS = [
  { value: 100000000, label: "Funzionante", icon: "✅" },
  { value: 100000001, label: "Guasta", icon: "❌" },
  { value: 100000002, label: "In manutenzione", icon: "🔧" },
  { value: 100000003, label: "Dismessa", icon: "🚫" },
];

function NuovaApparecchiaturaPage() {
  const navigate = useNavigate();
  const { id } = useParams(); // Se presente, siamo in modifica
  const [searchParams] = useSearchParams();
  const { user, apiRequest } = useAuth();
  const { showToast } = useToast();

  const isEdit = !!id;
  const rsaIdParam = searchParams.get("rsaId");
  const ubicazioneIdParam = searchParams.get("ubicazioneId");
  const immobileIdParam = searchParams.get("immobileId");
  const tipoParam = searchParams.get("tipo");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rsaList, setRsaList] = useState([]);
  const [immobiliList, setImmobiliList] = useState([]);
  const [areeList, setAreeList] = useState([]);
  const [fornitoriList, setFornitoriList] = useState([]);
  const [loadingImmobili, setLoadingImmobili] = useState(false);
  const [loadingAree, setLoadingAree] = useState(false);

  const [formData, setFormData] = useState({
    nome: "",
    codiceSeriale: "",
    matricolaInterna: "",
    marca: "",
    modello: "",
    tipoApparecchiatura: tipoParam || "",
    stato: 100000000, // Default: Funzionante
    dataInstallazione: "",
    dataScadenzaGaranzia: "",
    dataUltimaManutenzione: "",
    dataProssimaManutenzione: "",
    certificatoConformita: false,
    librettoManutenzione: false,
    attivo: true,
    note: "",
    rsaId: rsaIdParam || "",
    immobileId: immobileIdParam || "",
    ubicazioneId: ubicazioneIdParam || "",
    fornitoreAcquistoId: "",
    fornitoreManutenzioneId: "",
  });

  const [errors, setErrors] = useState({});

  // Carica dati iniziali
  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        // Carica RSA
        const rsaData = await apiRequest("/rsa");
        setRsaList(rsaData.value || []);

        // Carica fornitori
        const fornitoriData = await apiRequest("/fornitori");
        setFornitoriList(fornitoriData.value || []);

        // Se in modifica, carica dati apparecchiatura
        if (isEdit) {
          const appData = await apiRequest(`/apparecchiature/${id}`);
          setFormData({
            nome: appData.nome || "",
            codiceSeriale: appData.codiceSeriale || "",
            matricolaInterna: appData.matricolaInterna || "",
            marca: appData.marca || "",
            modello: appData.modello || "",
            tipoApparecchiatura: appData.tipoApparecchiatura ?? "",
            stato: appData.stato ?? 100000000,
            dataInstallazione: appData.dataInstallazione?.split("T")[0] || "",
            dataScadenzaGaranzia:
              appData.dataScadenzaGaranzia?.split("T")[0] || "",
            dataUltimaManutenzione:
              appData.dataUltimaManutenzione?.split("T")[0] || "",
            dataProssimaManutenzione:
              appData.dataProssimaManutenzione?.split("T")[0] || "",
            certificatoConformita: appData.certificatoConformita || false,
            librettoManutenzione: appData.librettoManutenzione || false,
            attivo: appData.attivo ?? true,
            note: appData.note || "",
            rsaId: appData.rsaId || "",
            immobileId: appData.immobileId || "",
            ubicazioneId: appData.ubicazioneId || "",
            fornitoreAcquistoId: appData.fornitoreAcquistoId || "",
            fornitoreManutenzioneId: appData.fornitoreManutenzioneId || "",
          });

          // Carica immobili per la RSA selezionata
          if (appData.rsaId) {
            const immobiliData = await apiRequest(
              `/rsa/${appData.rsaId}/immobili`,
            );
            setImmobiliList(immobiliData.value || []);
            // Carica aree filtrate per immobile (se specificato)
            if (appData.immobileId) {
              const areeData = await apiRequest(
                `/rsa/${appData.rsaId}/aree?immobileId=${appData.immobileId}`,
              );
              setAreeList(areeData.value || []);
            }
          }
        } else if (rsaIdParam) {
          // Carica immobili per la RSA preselezionata
          const immobiliData = await apiRequest(`/rsa/${rsaIdParam}/immobili`);
          setImmobiliList(immobiliData.value || []);
        }
      } catch (error) {
        console.error("Error loading data:", error);
        showToast("Errore nel caricamento dei dati", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [id, isEdit, rsaIdParam, apiRequest, showToast]);

  // Quando cambia RSA, ricarica immobili e resetta aree
  useEffect(() => {
    const loadRsaImmobili = async () => {
      if (!formData.rsaId) {
        setImmobiliList([]);
        setAreeList([]);
        setFormData((prev) => ({ ...prev, immobileId: "", ubicazioneId: "" }));
        return;
      }

      setLoadingImmobili(true);
      try {
        const immobiliData = await apiRequest(
          `/rsa/${formData.rsaId}/immobili`,
        );
        setImmobiliList(immobiliData.value || []);
        // Reset immobile e area se non appartengono alla nuova RSA
        if (formData.immobileId) {
          const foundImm = (immobiliData.value || []).find(
            (i) => i.id === formData.immobileId,
          );
          if (!foundImm) {
            setFormData((prev) => ({
              ...prev,
              immobileId: "",
              ubicazioneId: "",
            }));
            setAreeList([]);
          }
        } else {
          setAreeList([]);
          setFormData((prev) => ({ ...prev, ubicazioneId: "" }));
        }
      } catch (error) {
        console.error("Error loading immobili:", error);
      } finally {
        setLoadingImmobili(false);
      }
    };

    loadRsaImmobili();
  }, [formData.rsaId, apiRequest]);

  // Quando cambia immobile, ricarica aree filtrate
  useEffect(() => {
    const loadAreeByImmobile = async () => {
      if (!formData.rsaId || !formData.immobileId) {
        setAreeList([]);
        if (formData.ubicazioneId) {
          setFormData((prev) => ({ ...prev, ubicazioneId: "" }));
        }
        return;
      }

      setLoadingAree(true);
      try {
        const areeData = await apiRequest(
          `/rsa/${formData.rsaId}/aree?immobileId=${formData.immobileId}`,
        );
        setAreeList(areeData.value || []);
        // Reset ubicazione se non appartiene al nuovo immobile
        if (formData.ubicazioneId) {
          const found = (areeData.value || []).find(
            (a) => a.id === formData.ubicazioneId,
          );
          if (!found) {
            setFormData((prev) => ({ ...prev, ubicazioneId: "" }));
          }
        }
      } catch (error) {
        console.error("Error loading aree:", error);
      } finally {
        setLoadingAree(false);
      }
    };

    loadAreeByImmobile();
  }, [formData.immobileId, formData.rsaId, apiRequest]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    // Clear error on change
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.nome.trim()) {
      newErrors.nome = "Il nome è obbligatorio";
    }

    if (!formData.rsaId) {
      newErrors.rsaId = "La RSA è obbligatoria";
    }

    if (formData.tipoApparecchiatura === "") {
      newErrors.tipoApparecchiatura = "Il tipo è obbligatorio";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      showToast("Correggi gli errori nel form", "error");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        tipoApparecchiatura: Number(formData.tipoApparecchiatura),
        stato: Number(formData.stato),
        immobileId: formData.immobileId || null,
        ubicazioneId: formData.ubicazioneId || null,
        fornitoreAcquistoId: formData.fornitoreAcquistoId || null,
        fornitoreManutenzioneId: formData.fornitoreManutenzioneId || null,
        dataInstallazione: formData.dataInstallazione || null,
        dataScadenzaGaranzia: formData.dataScadenzaGaranzia || null,
        dataUltimaManutenzione: formData.dataUltimaManutenzione || null,
        dataProssimaManutenzione: formData.dataProssimaManutenzione || null,
      };

      if (isEdit) {
        await apiRequest(`/apparecchiature/${id}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
        showToast("Apparecchiatura aggiornata con successo", "success");
        navigate(`/apparecchiature/${id}`);
      } else {
        const result = await apiRequest("/apparecchiature", {
          method: "POST",
          body: JSON.stringify(payload),
        });
        showToast("Apparecchiatura creata con successo", "success");
        navigate(`/apparecchiature/${result.id}`);
      }
    } catch (error) {
      console.error("Error saving apparecchiatura:", error);
      showToast(error.message || "Errore nel salvataggio", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="spinner w-12 h-12"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEdit ? "Modifica Apparecchiatura" : "Nuova Apparecchiatura"}
          </h1>
          <p className="text-gray-500 mt-1">
            {isEdit
              ? "Modifica i dati dell'apparecchiatura"
              : "Inserisci i dati della nuova apparecchiatura"}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Informazioni Base */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Informazioni Base
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome / Descrizione *
              </label>
              <input
                type="text"
                name="nome"
                value={formData.nome}
                onChange={handleChange}
                className={`w-full rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 ${errors.nome ? "border-red-300" : "border-gray-300"}`}
                placeholder="Es: Ascensore principale, Caldaia centrale..."
              />
              {errors.nome && (
                <p className="mt-1 text-sm text-red-600">{errors.nome}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo Apparecchiatura *
              </label>
              <select
                name="tipoApparecchiatura"
                value={formData.tipoApparecchiatura}
                onChange={handleChange}
                className={`w-full rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 ${errors.tipoApparecchiatura ? "border-red-300" : "border-gray-300"}`}
              >
                <option value="">Seleziona tipo...</option>
                {TIPO_APPARECCHIATURA_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.icon} {opt.label}
                  </option>
                ))}
              </select>
              {errors.tipoApparecchiatura && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.tipoApparecchiatura}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Stato
              </label>
              <select
                name="stato"
                value={formData.stato}
                onChange={handleChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500"
              >
                {STATO_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.icon} {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Marca / Produttore
              </label>
              <input
                type="text"
                name="marca"
                value={formData.marca}
                onChange={handleChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500"
                placeholder="Es: Otis, Vaillant, Daikin..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Modello
              </label>
              <input
                type="text"
                name="modello"
                value={formData.modello}
                onChange={handleChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
        </Card>

        {/* Codici Identificativi */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Codici Identificativi
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Matricola Interna
              </label>
              <input
                type="text"
                name="matricolaInterna"
                value={formData.matricolaInterna}
                onChange={handleChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500 font-mono"
                placeholder="Codice inventario aziendale"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Numero di Serie (S/N)
              </label>
              <input
                type="text"
                name="codiceSeriale"
                value={formData.codiceSeriale}
                onChange={handleChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500 font-mono"
                placeholder="Serial number del produttore"
              />
            </div>
          </div>
        </Card>

        {/* Ubicazione */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Ubicazione
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                RSA *
              </label>
              <select
                name="rsaId"
                value={formData.rsaId}
                onChange={handleChange}
                className={`w-full rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 ${errors.rsaId ? "border-red-300" : "border-gray-300"}`}
              >
                <option value="">Seleziona RSA...</option>
                {rsaList.map((rsa) => (
                  <option key={rsa.id} value={rsa.id}>
                    {rsa.nome}
                  </option>
                ))}
              </select>
              {errors.rsaId && (
                <p className="mt-1 text-sm text-red-600">{errors.rsaId}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Immobile
              </label>
              <select
                name="immobileId"
                value={formData.immobileId}
                onChange={handleChange}
                disabled={!formData.rsaId || loadingImmobili}
                className="w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
              >
                <option value="">
                  {!formData.rsaId
                    ? "Seleziona prima la RSA"
                    : loadingImmobili
                      ? "Caricamento..."
                      : "Nessuno"}
                </option>
                {immobiliList.map((imm) => (
                  <option key={imm.id} value={imm.id}>
                    {imm.nome}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Opzionale: seleziona l'immobile di riferimento
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Area / Ubicazione
              </label>
              <select
                name="ubicazioneId"
                value={formData.ubicazioneId}
                onChange={handleChange}
                disabled={!formData.immobileId || loadingAree}
                className="w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
              >
                <option value="">
                  {!formData.rsaId
                    ? "Seleziona prima la RSA"
                    : !formData.immobileId
                      ? "Seleziona prima l'immobile"
                      : loadingAree
                        ? "Caricamento..."
                        : "Nessuna (generale)"}
                </option>
                {areeList.map((area) => (
                  <option key={area.id} value={area.id}>
                    {area.tipoAreaLabel}: {area.nome}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Opzionale: seleziona l'area/stanza dove si trova
                l'apparecchiatura
              </p>
            </div>
          </div>
        </Card>

        {/* Fornitori */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Fornitori
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fornitore Acquisto
              </label>
              <select
                name="fornitoreAcquistoId"
                value={formData.fornitoreAcquistoId}
                onChange={handleChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Nessuno</option>
                {fornitoriList.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.nome}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Fornitore da cui è stata acquistata
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fornitore Manutenzione
              </label>
              <select
                name="fornitoreManutenzioneId"
                value={formData.fornitoreManutenzioneId}
                onChange={handleChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Nessuno</option>
                {fornitoriList.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.nome}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Fornitore che effettua le manutenzioni
              </p>
            </div>
          </div>
        </Card>

        {/* Date */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Date e Scadenze
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data Installazione
              </label>
              <input
                type="date"
                name="dataInstallazione"
                value={formData.dataInstallazione}
                onChange={handleChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Scadenza Garanzia
              </label>
              <input
                type="date"
                name="dataScadenzaGaranzia"
                value={formData.dataScadenzaGaranzia}
                onChange={handleChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ultima Manutenzione
              </label>
              <input
                type="date"
                name="dataUltimaManutenzione"
                value={formData.dataUltimaManutenzione}
                onChange={handleChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prossima Manutenzione
              </label>
              <input
                type="date"
                name="dataProssimaManutenzione"
                value={formData.dataProssimaManutenzione}
                onChange={handleChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
        </Card>

        {/* Documenti e Note */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Documenti e Note
          </h2>
          <div className="space-y-4">
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="certificatoConformita"
                  checked={formData.certificatoConformita}
                  onChange={handleChange}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">
                  Certificato di Conformita CE disponibile
                </span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="librettoManutenzione"
                  checked={formData.librettoManutenzione}
                  onChange={handleChange}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">
                  Libretto Uso e Manutenzione disponibile
                </span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="attivo"
                  checked={formData.attivo}
                  onChange={handleChange}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">
                  Apparecchiatura Attiva
                </span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Note
              </label>
              <textarea
                name="note"
                value={formData.note}
                onChange={handleChange}
                rows={4}
                className="w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500"
                placeholder="Note aggiuntive..."
              />
            </div>
          </div>
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
            disabled={saving}
          >
            Annulla
          </Button>
          <Button type="submit" disabled={saving}>
            {saving
              ? "Salvataggio..."
              : isEdit
                ? "Salva Modifiche"
                : "Crea Apparecchiatura"}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default NuovaApparecchiaturaPage;
